import { chapterReadingPosition } from "../utils/chapterReadingPosition";
import { shouldPinProjectMedia, projectMediaPinHeight } from "../utils/projectMediaLayout";
import "../assets/css/project-case-study.css";

export default {
  data() {
    return { chromeHeight: 0, activeChapter: "", chapterProgress: 0 };
  },
  mounted() {
    this._caseShell = this.$el.closest(".container");
    this._onScroll = () => {
      if (this._scrollFrame) return;
      this._scrollFrame = requestAnimationFrame(() => {
        this._scrollFrame = null;
        this.updateChapterPosition();
      });
    };
    this._measureLayout = () => {
      this._resizeFrame = null;
      const header = document.querySelector(".header");
      const overlays =
        header &&
        ["sticky", "fixed"].includes(getComputedStyle(header).position);
      this.chromeHeight = overlays
        ? Math.ceil(header.getBoundingClientRect().height)
        : 0;
      const measurements = [...this.$el.querySelectorAll(".case-stage-content")].map((stage) => {
        const mediaHeight = stage.offsetHeight;
        const pair = stage.closest('.case-pair');
        const copy = pair?.querySelector(':scope > .case-copy');
        const layout = {
          copyHeight: copy?.offsetHeight || 0,
          mediaHeight,
          viewportHeight: window.innerHeight,
          top: this.chromeHeight + 84,
        };
        const pinned = window.innerWidth > 1000 && shouldPinProjectMedia(layout);
        return { stage, mediaHeight, layout, pinned };
      });
      measurements.forEach(({ stage, mediaHeight, layout, pinned }) => {
        stage.style.setProperty("--case-stage-height", `${mediaHeight}px`);
        stage.classList.toggle('is-reading-pinned', pinned);
        if (stage.parentElement.matches('.case-stage')) {
          stage.parentElement.classList.toggle('has-reading-pinned', pinned);
          stage.parentElement.style.setProperty('--case-pin-height', `${projectMediaPinHeight(layout)}px`);
        }
      });
      this._onScroll();
    };
    // ResizeObserver delivers measurements before paint. Apply layout changes
    // in the next frame so a collapsing disclosure cannot resize its observed
    // ancestors again within the same observer delivery cycle.
    this._onResize = () => {
      if (this._resizeFrame) return;
      this._resizeFrame = requestAnimationFrame(this._measureLayout);
    };
    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(this.$el);
    this.$el
      .querySelectorAll(".case-stage-content, .case-pair > .case-copy")
      .forEach((stage) => this._resizeObserver.observe(stage));
    const header = document.querySelector(".header");
    if (header) this._resizeObserver.observe(header);
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onResize, { passive: true });
    this._onResize();
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.remove("case-reveal-pending");
            this._revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -24px 0px", threshold: 0 }
      );
      this.$el.querySelectorAll("[data-case-reveal]").forEach((element) => {
        if (element.getBoundingClientRect().top < window.innerHeight) return;
        element.classList.add("case-reveal-pending");
        this._revealObserver.observe(element);
      });
    }
  },
  beforeUnmount() {
    this._caseShell?.style.removeProperty("--case-return-shift");
    window.removeEventListener("scroll", this._onScroll);
    window.removeEventListener("resize", this._onResize);
    cancelAnimationFrame(this._scrollFrame);
    cancelAnimationFrame(this._resizeFrame);
    this._resizeObserver?.disconnect();
    this._revealObserver?.disconnect();
  },
  methods: {
    updateChapterPosition() {
      const sections = [...this.$el.querySelectorAll("[data-case-chapter]")];
      const nav = this.$refs.chapterNav.$el;
      const back = this._caseShell?.querySelector(".site-back-global, .view-return-slot");
      if (back) {
        const navRect = nav.getBoundingClientRect();
        const backRect = back.getBoundingClientRect();
        const baseTop = parseFloat(getComputedStyle(back).top) || 0;
        // The rail can have empty space beside its links on wide screens.
        // Only the visible link area needs clearance from the return control.
        const sharesHorizontalSpace = [...nav.querySelectorAll(".case-nav-link")].some((link) => {
          const rect = link.getBoundingClientRect();
          const left = Math.max(rect.left, navRect.left);
          const right = Math.min(rect.right, navRect.right);
          return right > left && left < backRect.right + 8 && right > backRect.left - 8;
        });
        // Use its unshifted top so moving the control cannot undo the decision.
        const approaching = navRect.top <= baseTop + back.offsetHeight + 16;
        const shift = sharesHorizontalSpace && approaching
          ? Math.max(0, navRect.bottom + 8 - baseTop)
          : 0;
        this._caseShell.style.setProperty("--case-return-shift", `${shift}px`);
      }
      const offset =
        this.chromeHeight + this.$refs.chapterNav.$el.offsetHeight + 36;
      const state = chapterReadingPosition(
        sections.map((section) => ({
          id: section.id,
          top: section.getBoundingClientRect().top + window.scrollY,
        })),
        window.scrollY,
        document.documentElement.scrollHeight - window.innerHeight,
        offset
      );
      if (this.activeChapter !== state.id) {
        this.$nextTick(() => this.$refs.chapterNav.revealCurrent());
      }
      this.activeChapter = state.id;
      this.chapterProgress = state.progress;
    },
    goToChapter(id, event) {
      const section = document.getElementById(id);
      if (!section) return;
      this.activeChapter = id;
      section
        .querySelectorAll(".case-reveal-pending")
        .forEach((element) => element.classList.remove("case-reveal-pending"));
      const top =
        window.scrollY +
        section.getBoundingClientRect().top -
        this.chromeHeight -
        this.$refs.chapterNav.$el.offsetHeight -
        28;
      const instant =
        event.detail === 0 ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top, behavior: instant ? "instant" : "smooth" });
      const heading = section.querySelector("h2");
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    },
  },
};
